import { useQuery } from '@tanstack/react-query';
import { useFilterContext } from '../state';
import { appQueries } from '../../api/app.api';
import { IdentityTaskDto, ResourceType, UserSettingsDto } from '../../api/app.types';
import { Auth } from '@easylife365/react/auth';
import { useFilteredTasks } from '../state/filter-tasks';
import { sameId } from '../ids';

export default function useAdditionalAppIds() {
  const { showAllApps } = useFilterContext();

  const { data: mySettings } = useQuery({
    ...appQueries.getMySettings,
    enabled: !showAllApps,
  });

  const { data: allTasks } = useQuery({
    ...appQueries.getTasks,
    enabled: !showAllApps,
  });

  const tasks = useFilteredTasks(allTasks);
  return getAdditionalAppIds(showAllApps, mySettings, tasks);
}

function getAdditionalAppIds(
  showAllApps: boolean,
  mySettings: UserSettingsDto | null | undefined,
  tasks: IdentityTaskDto[] | undefined
) {
  if (showAllApps)
    return {
      additionalAppRegistrationIds: [],
      additionalEnterpriseApplicationIds: [],
    };

  const additionalAppRegistrationIds = new Set<string>();
  const additionalEnterpriseApplicationIds = new Set<string>();

  if (mySettings) {
    const ownerAppEntries = Object.entries(mySettings.technicalOwnershipSettings.ownerApps).concat(
      Object.entries(mySettings.businessOwnershipSettings.ownerApps)
    );

    for (const ownerAppEntry of ownerAppEntries) {
      const [id, app] = ownerAppEntry;
      switch (app.appType) {
        case ResourceType.AppRegistration:
          additionalAppRegistrationIds.add(id);
          break;

        case ResourceType.EnterpriseApplication:
          additionalEnterpriseApplicationIds.add(id);
          break;
      }
    }
  }

  if (tasks) {
    for (const task of tasks) {
      if (!sameId(task.ownerId, Auth.getInstance().getUserAccount()?.id)) continue;
      if (!task.appObjectId) continue; // Rows from before the application id was recorded have none.

      // TODO: Switch on task.appType when we have it
      additionalAppRegistrationIds.add(task.appObjectId);
      additionalEnterpriseApplicationIds.add(task.appObjectId);
    }
  }

  return {
    additionalAppRegistrationIds: [...additionalAppRegistrationIds.values()],
    additionalEnterpriseApplicationIds: [...additionalEnterpriseApplicationIds.values()],
  };
}
